import type { Cell, ConceptRow } from '@balance/domain';
import { Link } from 'react-router-dom';
import { dateLabel, eur, periodLabel } from '../format.js';
import { useApp } from '../state.js';
import { Modal } from './Modal.js';
import { Badge, ConceptName, STATUS_LABEL } from './ui.js';

/** Detalle de un importe: movimientos y previsión que lo componen (distinto de la ficha de reglas). */
export function CellDetail({ row, cell, onClose }: { row: ConceptRow; cell: Cell; onClose: () => void }) {
  const { movements, catalog } = useApp();
  const allocations = (movements?.allocations ?? []).filter((a) => cell.allocationIds.includes(a.id));
  const txById = new Map((movements?.transactions ?? []).map((t) => [t.id, t]));
  return (
    <Modal title={`${row.name} · ${periodLabel(cell.period)}`} subtitle="Qué compone esta cifra" onClose={onClose}>
      <div className="inline" style={{ marginBottom: 14 }}>
        <span className="big">{eur(cell.projectedCents)}</span>
        <Badge kind={cell.status === 'over' ? 'red' : cell.status === 'actual' ? '' : 'neutral'}>{STATUS_LABEL[cell.status]}</Badge>
        {cell.overridden ? <Badge kind="warn">ajuste de este mes</Badge> : null}
      </div>
      <dl className="kv">
        <dt>Concepto</dt>
        <dd>
          <ConceptName id={row.conceptId} name={row.name} /> <span className="muted">· {row.categoryName}</span>
        </dd>
        <dt>Previsto</dt>
        <dd>{eur(cell.expectedCents)}{cell.baseExpectedCents !== null && cell.baseExpectedCents !== cell.expectedCents ? <span className="muted"> (dotación original {eur(cell.baseExpectedCents)})</span> : null}</dd>
        <dt>Real imputado</dt>
        <dd>{eur(cell.actualCents)}</dd>
        {cell.varianceCents !== null && cell.varianceCents !== 0 ? (
          <>
            <dt>Diferencia con lo planificado</dt>
            <dd className={cell.varianceCents > 0 ? 'positive' : 'negative'}>{eur(cell.varianceCents, { sign: true })}</dd>
          </>
        ) : null}
        {cell.overCents > 0 ? (
          <>
            <dt>Exceso</dt>
            <dd className="negative">{eur(cell.overCents)}</dd>
          </>
        ) : null}
        {cell.financingCents ? (
          <>
            <dt>Préstamo recibido</dt>
            <dd>{eur(cell.financingCents)} · suma al acumulado, no al resto</dd>
          </>
        ) : null}
      </dl>
      <h3 style={{ marginTop: 18 }}>Explicación</h3>
      <ul className="muted">
        {cell.explanation.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
      {cell.occurrences.length ? (
        <>
          <h3>Vencimientos del mes</h3>
          {cell.occurrences.map((o) => (
            <div className="row" key={o.key}>
              <div>
                {dateLabel(o.date)} {o.covered ? <Badge>cubierto</Badge> : <Badge kind="neutral">previsto</Badge>}
                {o.instalmentNumber ? <span className="muted"> · cuota {o.instalmentNumber}/{o.instalmentTotal}</span> : null}
              </div>
              <span className="number">{o.covered && o.actualCents ? `${eur(o.actualCents)} real` : eur(o.amountCents)}</span>
            </div>
          ))}
        </>
      ) : null}
      {cell.cancelled.length ? (
        <>
          <h3>Canceladas</h3>
          {cell.cancelled.map((c) => (
            <div className="row dim" key={c.key}>
              <div>
                <s>{dateLabel(c.originalDate)}</s> {c.note ? <span className="muted">· {c.note}</span> : null}
              </div>
              <span className="number">{eur(c.amountCents)}</span>
            </div>
          ))}
        </>
      ) : null}
      <h3 style={{ marginTop: 18 }}>Movimientos imputados</h3>
      {allocations.length === 0 ? <p className="muted">Ningún movimiento real imputado todavía.</p> : null}
      {allocations.map((a) => {
        const t = txById.get(a.transactionId);
        return (
          <div className="row" key={a.id}>
            <div>
              {t ? (
                <Link to={`/movimientos?abrir=${t.id}`} onClick={onClose}>
                  {t.description}
                </Link>
              ) : (
                'Movimiento'
              )}
              <div className="muted">
                {t ? dateLabel(t.date) : ''}
                {a.recoverableCents ? ` · ${eur(a.recoverableCents)} recuperables` : ''}
                {a.subPeriod ? ` · ${a.subPeriod.replace('weekend:', 'fin de semana del ')}` : ''}
                {a.note ? ` · ${a.note}` : ''}
              </div>
            </div>
            <span className="number">{eur(a.amountCents)}</span>
          </div>
        );
      })}
      <p className="muted" style={{ marginTop: 16 }}>
        {catalog ? 'Los importes cambian al editar movimientos, ajustes mensuales o condiciones del concepto.' : ''}
      </p>
    </Modal>
  );
}
